import React from 'react';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import {
    Toast,
    localStorageJsonParseDecrypt,
    localStorageEncrypt,
    mainWebsite,
    getAccessToken,
    getIncserveToken,
    clearCookies,
    clearOldCookies,
    getTenantId,
} from '../../utils/helper';
import {
    LOGIN_USER,
    GET_USER_PERMISSION,
    LOGOUT_USER,
    GET_NOTIFICATION_LIST,
    READ_NOTIFICATION_LIST,
    CLEAR_NOTIFICATION_LIST,
    GET_PRODUCT_LIST,
    FETCH_LOGO,
    GET_SUBSCRIPTION_DATA,
    GET_CONFIGURATIONS,
    GET_USER_INFORMATION,
    GET_CONFIGURATIONS_FOR_INSTRUCTIONS,
    LOGIN_USER_DEVICE_CHECK,
    SUBMIT_NPS,
    NPS_HANDLE,
    // CSAT
    SUBMIT_CSAT,
    CSAT_HANDLE,
} from '../action/types';
import {
    loginUserFailure,
    loginUserSuccess,
    getUserPermissionSuccess,
    getUserPermissionFailure,
    logoutUserSuccess,
    logoutUserFailure,
    getNotificationSuccess,
    getNotificationFailure,
    readNotificationSuccess,
    readNotificationFailure,
    clearNotificationSuccess,
    clearNotificationFailure,
    getProductListSuccess,
    getProductListFailure,
    fetchLogoSuccess,
    fetchLogoFailure,
    getSubscriptionDataSuccess,
    getSubscriptionDataFailure,
    getConfigurationsSuccess,
    getConfigurationsFailure,
    getUserInformationSuccess,
    getUserInformationFailure,
    getConfigurationsForInstructionsSuccess,
    getConfigurationsForInstructionsFailure,
    loginUserDeviceCheckSuccess,
    loginUserDeviceCheckFailure,
    submitNpsSurveySuccess,
    submitNpsSurveyFailure,
    npsHandleSuccess,
    // CSAT
    submitCsatSuccess,
    submitCsatFailure,
    handleCsatSuccess,
    // Candidates (for CSAT refresh)
    getCandidate,
} from '../action';
import AUTH from '../../utils/api/auth';
import PRODUCT from '../../utils/api/product';
import USERS from '../../utils/api/users';
import INTERVIEW from '../../utils/api/interview';

function* fetchLogoRequest() {
    try {
        const res = yield USERS.get('users/getLogo');
        if (res.status === 200 || res.status === 201) {
            yield put(fetchLogoSuccess(res.data.result));
        }
    } catch (e) {
        yield put(fetchLogoFailure());
    }
}
function* loginUserRequest(action) {
    try {
        const res = yield AUTH.get('auth/oauth/verify-token');
        if (res && res.status === 200) {
            yield put(loginUserSuccess(res.data.result.user));
            if (res.data?.result?.user?.isVerified) {
                if (
                    res.data?.result?.user?.userType === 'employer' ||
                    res.data?.result?.user?.userType === 'subemployer'
                ) {
                    localStorage.clear();
                    clearOldCookies();
                    localStorageEncrypt('userData', JSON.stringify(res.data.result.user));
                    localStorageEncrypt('companyData', JSON.stringify(res.data?.result?.businessDetails));
                    localStorageEncrypt('permissions', JSON.stringify(res.data.result.permissions));
                    yield call(action.payload.callback);
                } else {
                    yield put(loginUserFailure({ data: { message: 'Authorisation Denied' } }));
                }
            } else {
                localStorageEncrypt('userData', JSON.stringify(res.data.result.user));
                yield call(action.payload.callback);
            }
        }
    } catch (e) {
        yield put(loginUserFailure(e.response));
    }
}

function* getUserPermissionRequest() {
    const tenantId = getTenantId();
    try {
        const res = yield USERS.get(`users/permissions`, {
            headers: {
                Authorization: `Bearer ${getAccessToken}`,
                ...(tenantId && { 'x-tenant-id': tenantId }),
            },
        });
        if (res.status === 200) {
            yield put(getUserPermissionSuccess(res.data.result));
            localStorageEncrypt('permissions', JSON.stringify(res.data.result));
        }
    } catch (e) {
        yield put(getUserPermissionFailure());
    }
}
function* getProductListRequest() {
    try {
        let res;
        if (getIncserveToken) {
            res = yield PRODUCT.post(`products/productList`, { incserveToken: getIncserveToken });
        } else {
            res = yield PRODUCT.get(`products/productList`);
        }
        if (res.status === 200 || res.status === 201) {
            yield put(getProductListSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getProductListFailure());
        yield e?.response?.data?.message &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}
function* logoutUserRequest(action) {
    try {
        const resLogout = yield USERS.post('users/nps/handle', { action: 'LOGOUT' });
        const res = yield USERS.patch(`auth/logout`);
        if (res.status === 201 || res.status === 200) {
            yield put(logoutUserSuccess());
            yield toast.success(<Toast msg="Logged out successfully." />, {
                autoClose: 5000,
                toastId: 'Logged out successfully.',
            });
            localStorage.clear();
            clearCookies();
            window.location.replace(mainWebsite + '/employer/login');
        }
    } catch (e) {
        yield put(logoutUserFailure());
        yield toast.success(<Toast msg="Logged out successfully." />, {
            autoClose: 5000,
            toastId: 'Logged out successfully.',
        });
        localStorage.clear();
        clearCookies();
        window.location.replace(mainWebsite + '/employer/login');
    }
}
function* getNotificationRequest(action) {
    try {
        const isPageAndPageSize =
            action.payload.page && action.payload.pageSize
                ? `?page=${action.payload.page}&pageSize=${action.payload.pageSize}`
                : '';
        const isSearch = action.payload.search ? `&search=${encodeURIComponent(action.payload.search)}` : '';
        const res = yield USERS.get(`users/notification${isPageAndPageSize}${isSearch}`);
        if (res.status === 200) {
            yield put(getNotificationSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getNotificationFailure());
    }
}

function* readNotificationRequest(action) {
    try {
        const res = yield USERS.put(`users/notification/readAll`);
        if (res.status === 200) {
            yield put(readNotificationSuccess());
            yield call(action.payload.callback());
        }
    } catch (e) {
        yield put(readNotificationFailure());
    }
}

function* clearNotificationRequest(action) {
    try {
        const isNotificationId = action.payload.id ? `?id=${action.payload.id}` : '';
        const res = yield USERS.delete(`users/notification${isNotificationId}`);
        if (res.status === 204) {
            yield put(clearNotificationSuccess());
            yield call(action.payload.callback());
        }
    } catch (e) {
        yield put(clearNotificationFailure());
    }
}

function* getSubscriptionDataRequest() {
    try {
        const res = yield PRODUCT.get(`orders/getIncbotOrder`);
        if (res.status === 200) {
            yield put(getSubscriptionDataSuccess(res?.data?.result));
        }
    } catch (e) {
        yield put(getSubscriptionDataFailure());
        yield e?.response?.data?.message &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}

function* getConfigurationsRequest() {
    try {
        const res = yield AUTH.get(`users/incbot/userSettings`);
        if (res.status === 201 || res.status === 200) {
            yield put(getConfigurationsSuccess(res.data?.result));
        }
    } catch (e) {
        yield put(getConfigurationsFailure());
        yield e?.response?.data?.message &&
            toast.error(<Toast msg={e?.response?.data?.message} />, { toastId: e?.response?.data?.message });
    }
}
function* getConfigurationsRequestForInstructions() {
    try {
        const res = yield AUTH.get(`users/v2/userSettings`);
        if (res.status === 201 || res.status === 200) {
            yield put(getConfigurationsForInstructionsSuccess(res.data?.result));
        }
    } catch (e) {
        yield put(getConfigurationsForInstructionsFailure());
        yield e?.response?.data?.message &&
            toast.error(<Toast msg={e?.response?.data?.message} />, { toastId: e?.response?.data?.message });
    }
}

function* getUserInformationRequest(action) {
    try {
        const res = yield USERS.get(`users/${action.payload.id}`);
        if (res.status === 200) {
            yield put(getUserInformationSuccess(res.data.result));
            yield call(action.payload.callback, res.data.result);
        }
    } catch (e) {
        yield put(getUserInformationFailure());
    }
}

function* loginUserDeviceCheckRequest(action) {
    try {
        const res = yield AUTH.get('auth/oauth/verify-token/device-check', {
            headers: {
                'x-visitorid': action.payload.visitorId,
                'x-devicetype': action.payload.deviceType,
            },
        });
        if (res.status === 200 || res.status === 201) {
            yield put(loginUserDeviceCheckSuccess(res.data?.result));
            if (action.payload.callback) {
                yield call(action.payload.callback, res.data?.result);
            }
        }
    } catch (e) {
        yield put(loginUserDeviceCheckFailure(e.response?.data || { message: 'Device check failed' }));
        if (action.payload.callback) {
            yield call(action.payload.callback, {
                success: false,
                message: e.response?.data?.message || 'Device check failed',
            });
        }
    }
}

export function* watchGetSubscriptionDataAPI() {
    yield takeEvery(GET_SUBSCRIPTION_DATA, getSubscriptionDataRequest);
}

export function* watchGetConfigurationsAPI() {
    yield takeEvery(GET_CONFIGURATIONS, getConfigurationsRequest);
}
export function* watchGetConfigurationsForInstructionsAPI() {
    yield takeEvery(GET_CONFIGURATIONS_FOR_INSTRUCTIONS, getConfigurationsRequestForInstructions);
}
export function* watchFetchLogoAPI() {
    yield takeEvery(FETCH_LOGO, fetchLogoRequest);
}
export function* watchGetUserPermissionAPI() {
    yield takeEvery(GET_USER_PERMISSION, getUserPermissionRequest);
}
export function* watchGetProductListAPI() {
    yield takeEvery(GET_PRODUCT_LIST, getProductListRequest);
}
export function* watchLogoutUserAPI() {
    yield takeEvery(LOGOUT_USER, logoutUserRequest);
}
export function* watchGetNotificationAPI() {
    yield takeEvery(GET_NOTIFICATION_LIST, getNotificationRequest);
}
export function* watchReadNotificationAPI() {
    yield takeEvery(READ_NOTIFICATION_LIST, readNotificationRequest);
}
export function* watchClearNotificationAPI() {
    yield takeEvery(CLEAR_NOTIFICATION_LIST, clearNotificationRequest);
}
export function* watchLoginUserAPI() {
    yield takeEvery(LOGIN_USER, loginUserRequest);
}
export function* watchGetUserInformationAPI() {
    yield takeEvery(GET_USER_INFORMATION, getUserInformationRequest);
}

export function* watchLoginUserDeviceCheckAPI() {
    yield takeEvery(LOGIN_USER_DEVICE_CHECK, loginUserDeviceCheckRequest);
}

const EMOJI_RATING_TO_NUMBER = {
    very_dissatisfied: 1,
    dissatisfied: 2,
    neutral: 3,
    satisfied: 4,
    very_satisfied: 5,
};

/**
 * Handle NPS actions such as LOGIN (decide whether to show the popup)
 * and SKIP / LOGOUT etc. We also make sure to update the local
 * `npsHandleResult` state for non-LOGIN actions so that the popup
 * state is immediately in sync and does not briefly re-open on refresh
 * with stale persisted data.
 */

function* npsHandleRequest(action) {
    try {
        const { action: npsAction, productId } = action.payload || {};
        if (!npsAction || !productId) return;

        const res = yield USERS.post('users/nps/handle', { action: npsAction, productId });
        const result = res?.data?.result;

        // If backend sends a result (e.g. { showPopup, reason }), always trust it
        if (result != null) {
            yield put(npsHandleSuccess(result));
            return;
        }

        // For non-LOGIN actions like SKIP / LOGOUT, if backend does not
        // return anything, force-hide the popup locally so that a refresh
        // does not briefly reopen it based on old persisted state.
        if (npsAction !== 'LOGIN') {
            yield put(
                npsHandleSuccess({
                    showPopup: false,
                    reason: npsAction,
                })
            );
        }
    } catch (e) {
        // On failure for SKIP / LOGOUT etc., still make sure we don't
        // keep showing the popup if the user explicitly dismissed it.
        const npsAction = action.payload?.action;
        if (npsAction && npsAction !== 'LOGIN') {
            yield put(
                npsHandleSuccess({
                    showPopup: false,
                    reason: npsAction,
                })
            );
        }
        // Fire-and-forget; no UI feedback on failure
    }
}

function* submitNpsRequest(action) {
    try {
        const { productId, answers, callback } = action.payload || {};
        const answersPayload = [
            {
                question: 'recommendation',
                answer: answers?.recommendScore ?? 0,
                comments: answers?.recommendFeedback?.trim() ?? '',
            },
            {
                question: 'service',
                answer: EMOJI_RATING_TO_NUMBER[answers?.serviceRating] ?? 0,
                comments: answers?.serviceFeedback?.trim() ?? '',
            },
            {
                question: 'platform',
                answer: EMOJI_RATING_TO_NUMBER[answers?.platformRating] ?? 0,
                comments: answers?.platformFeedback?.trim() ?? '',
            },
            { question: 'comment', answer: answers?.suggestions?.trim() ?? '' },
        ];
        const res = yield USERS.post('users/nps/submit', { productId, responses: answersPayload });
        if (res.status === 200 || res.status === 201) {
            const { message, category } = res.data || {};
            const successMessage = [message || 'NPS submitted successfully', category].filter(Boolean).join(' • ');
            toast.success(successMessage);
            yield put(submitNpsSurveySuccess());
            yield put(
                npsHandleSuccess({
                    showPopup: false,
                    reason: 'SUBMIT',
                })
            );
            if (typeof callback === 'function') {
                yield call(callback);
            }
        } else {
            yield put(submitNpsSurveyFailure());
            toast.error('Failed to submit survey. Please try again.');
        }
    } catch (e) {
        yield put(submitNpsSurveyFailure());
        toast.error(e?.response?.data?.message || 'Failed to submit survey. Please try again.');
        if (typeof action.payload?.failureCallback === 'function') {
            yield call(action.payload.failureCallback);
        }
    }
}

export function* watchSubmitNpsAPI() {
    yield takeEvery(SUBMIT_NPS, submitNpsRequest);
}

export function* watchNpsHandleAPI() {
    yield takeEvery(NPS_HANDLE, npsHandleRequest);
}

/**
 * CSAT (Candidate Satisfaction) flow
 * Separate from employer NPS so that APIs and state
 * are isolated and can evolve independently.
 */

function* csatHandleRequest(action) {
    try {
        const { action: csatAction } = action.payload || {};
        if (!csatAction) return;

        const res = yield USERS.post('users/csat/handleCsat', { action: csatAction });
        const result = res?.data?.result;

        // If backend sends a result (e.g. { showPopup, reason }), always trust it
        if (result != null) {
            yield put(handleCsatSuccess(result));
            return;
        }

        // For non-LOGIN actions like SKIP / LOGOUT etc., if backend does not
        // return anything, force-hide the popup locally if consumer uses it.
        if (csatAction !== 'LOGIN') {
            yield put(
                handleCsatSuccess({
                    showPopup: false,
                    reason: csatAction,
                })
            );
        }
    } catch (e) {
        const csatAction = action.payload?.action;
        if (csatAction && csatAction !== 'LOGIN') {
            yield put(
                handleCsatSuccess({
                    showPopup: false,
                    reason: csatAction,
                })
            );
        }
        // Fire-and-forget; no UI feedback on failure
    }
}

function* submitCsatRequest(action) {
    try {
        const { answers, callback, interviewId, productId } = action.payload || {};
        const answersPayload = [
            {
                question: 'overallInterviewExperience',
                answer: answers?.recommendScore ?? 0,
                comments: answers?.recommendFeedback?.trim() ?? '',
            },
            {
                question: 'evaluationAndFeedbackQuality',
                answer: EMOJI_RATING_TO_NUMBER[answers?.serviceRating] ?? 0,
                comments: answers?.serviceFeedback?.trim() ?? '',
            },
            {
                question: 'schedulingAndCoordination',
                answer: EMOJI_RATING_TO_NUMBER[answers?.platformRating] ?? 0,
                comments: answers?.platformFeedback?.trim() ?? '',
            },
            { question: 'comment', answer: answers?.suggestions?.trim() ?? '' },
        ];

        const payload = { interviewId, productId, answers: answersPayload };
        const res = yield INTERVIEW.post('interviews/csat/submitCsat', payload);
        if (res.status === 200 || res.status === 201) {
            const { message } = res.data.result || {};
            toast.success(message || 'Feedback submitted successfully');
            yield put(submitCsatSuccess());
            // Refresh candidates list via existing saga so URL building stays dynamic
            yield put(
                getCandidate({
                    page: 1,
                    pageSize: 10,
                    search: '',
                    sort: '',
                    order: '',
                    activeTab: 'Completed Interviews',
                    silentRefresh: true,
                    filter: {
                        from: '',
                        to: '',
                        experience: '',
                        status: '',
                        sort: '',
                        createdBy: '',
                    },
                })
            );
            if (typeof callback === 'function') {
                yield call(callback);
            }
        } else {
            yield put(submitCsatFailure());
            toast.error('Failed to submit feedback. Please try again.');
        }
    } catch (e) {
        yield put(submitCsatFailure());
        toast.error(e?.response?.data?.message || 'Failed to submit feedback. Please try again.');
    }
}

export function* watchSubmitCsatAPI() {
    yield takeEvery(SUBMIT_CSAT, submitCsatRequest);
}

export function* watchCsatHandleAPI() {
    yield takeEvery(CSAT_HANDLE, csatHandleRequest);
}

export default function* rootSaga() {
    yield all([
        watchFetchLogoAPI(),
        watchLoginUserAPI(),
        watchGetUserPermissionAPI(),
        watchGetProductListAPI(),
        watchLogoutUserAPI(),
        watchGetNotificationAPI(),
        watchReadNotificationAPI(),
        watchClearNotificationAPI(),
        watchGetSubscriptionDataAPI(),
        watchGetConfigurationsAPI(),
        watchGetUserInformationAPI(),
        watchGetConfigurationsForInstructionsAPI(),
        watchLoginUserDeviceCheckAPI(),
        watchSubmitNpsAPI(),
        watchNpsHandleAPI(),
        // CSAT
        watchSubmitCsatAPI(),
        watchCsatHandleAPI(),
    ]);
}
